import React, { useEffect, useState } from 'react';
import {
  CalendarOff, Plus, Check, X, Clock, AlertTriangle, ChevronLeft,
  Save, Eye, Ban, CheckCircle2, XCircle, Info, FileText, History,
  Shield, ArrowRight, RotateCcw,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { NEU } from '../neuStyles';

// ── Constants ─────────────────────────────────────────────────────────────────

const LEAVE_TYPES = [
  { value: 'electronic',      label: 'Electronic Leave',       category: 'standard' },
  { value: 'medical',         label: 'Medical Leave',          category: 'medical' },
  { value: 'family_friendly', label: 'Family Friendly Leave',  category: 'standard' },
  { value: 'sick',            label: 'Sick Leave',             category: 'medical' },
  { value: 'bereavement',     label: 'Bereavement Leave',      category: 'standard' },
  { value: 'fmla',            label: 'FMLA Leave',             category: 'protected' },
  { value: 'jury_duty',       label: 'Jury Duty',              category: 'protected' },
  { value: 'military',        label: 'Military Leave',         category: 'protected' },
  { value: 'vacation_pto',    label: 'Vacation / PTO',         category: 'standard' },
  { value: 'unpaid',          label: 'Unpaid Personal Leave',  category: 'standard' },
  { value: 'administrative',  label: 'Administrative Leave',   category: 'admin' },
  { value: 'cme_training',    label: 'CME / Training',         category: 'standard' },
];

const REASON_CATEGORIES = [
  { value: 'personal',          label: 'Personal' },
  { value: 'medical',           label: 'Medical' },
  { value: 'family_care',       label: 'Family Care' },
  { value: 'bereavement',       label: 'Bereavement' },
  { value: 'legal',             label: 'Legal / Civic Duty' },
  { value: 'military_service',  label: 'Military Service' },
  { value: 'education',         label: 'Education / Training' },
  { value: 'administrative',    label: 'Administrative' },
  { value: 'other',             label: 'Other' },
];

const FMLA_DESIGNATIONS = [
  { value: 'fmla_standard',              label: 'FMLA Standard (12 weeks)' },
  { value: 'fmla_military_caregiver',    label: 'FMLA Military Caregiver (26 weeks)' },
  { value: 'fmla_qualifying_exigency',   label: 'FMLA Qualifying Exigency' },
];

const STATUS_CONFIG = {
  draft:                { label: 'Draft',               color: '#6b7280', bg: '#f9fafb', icon: FileText },
  pending_supervisor:   { label: 'Pending Supervisor',  color: '#f59e0b', bg: '#fffbeb', icon: Clock },
  pending_hr:           { label: 'Pending HR Review',   color: '#8b5cf6', bg: '#f5f3ff', icon: Clock },
  approved:             { label: 'Approved',            color: '#10b981', bg: '#ecfdf5', icon: CheckCircle2 },
  denied:               { label: 'Denied',              color: '#ef4444', bg: '#fef2f2', icon: XCircle },
  cancelled:            { label: 'Cancelled',           color: '#6b7280', bg: '#f9fafb', icon: Ban },
  expired:              { label: 'Expired',             color: '#9ca3af', bg: '#f3f4f6', icon: Clock },
  revoked:              { label: 'Revoked',             color: '#dc2626', bg: '#fef2f2', icon: XCircle },
};

const EMPTY_FORM = {
  leaveType: 'vacation_pto', startDate: '', endDate: '',
  hoursRequested: '', halfDay: '', isIntermittent: false,
  reasonCategory: 'personal', reason: '', urgency: 'standard',
  relationship: '', doctorNoteAttached: false,
  supervisorId: '', fmlaDesignation: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(d) {
  if (!d) return '—';
  const str = typeof d === 'string' ? d.split('T')[0] : d;
  return new Date(str + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function leaveLabel(type) {
  const t = LEAVE_TYPES.find(l => l.value === type);
  return t ? t.label : type?.replace(/_/g, ' ') || '—';
}

function daysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem',
      fontWeight: 600, color: cfg.color, background: cfg.bg,
      border: 'none',
    }}>
      <Icon size={13} /> {cfg.label}
    </span>
  );
}

// ── Balance Cards ─────────────────────────────────────────────────────────────

function BalanceCards({ balances }) {
  if (!balances || balances.length === 0) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
      {balances.map(b => {
        const available = Math.max(0, (b.total_hours + b.carryover_hours) - b.used_hours - b.pending_hours);
        const pct = b.total_hours > 0 ? Math.min(100, ((b.used_hours + b.pending_hours) / (b.total_hours + b.carryover_hours)) * 100) : 0;
        return (
          <div key={b.id} style={{
            ...NEU.cardSm,
            padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.35rem',
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {b.display_name || leaveLabel(b.leave_type)}
            </div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
              {available.toFixed(1)}<span style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 500 }}> hrs avail</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: '#f0f0f0', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 2,
                width: `${pct}%`,
                background: pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#10b981',
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
              {b.used_hours} used · {b.pending_hours > 0 ? `${b.pending_hours} pending · ` : ''}{b.total_hours} total
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Status History Timeline ──────────────────────────────────────────────────

function StatusTimeline({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem' }}>
        <History size={16} color="#6C757D" />
        <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Status History</span>
      </div>
      <div style={{ borderLeft: 'none', boxShadow: 'inset 1px 0 0 rgba(186,199,216,0.45)', paddingLeft: '1rem', marginLeft: '0.5rem' }}>
        {history.map((h, i) => (
          <div key={h.id} style={{ marginBottom: i < history.length - 1 ? '0.75rem' : 0, position: 'relative' }}>
            <div style={{
              position: 'absolute', left: '-1.375rem', top: '0.2rem',
              width: 10, height: 10, borderRadius: '50%',
              background: i === history.length - 1 ? '#2E8B98' : '#d1d5db',
            }} />
            <div style={{ fontSize: '0.82rem', fontWeight: 500, color: '#111827' }}>
              {h.old_status ? <><StatusBadge status={h.old_status} /> <ArrowRight size={12} style={{ verticalAlign: 'middle', margin: '0 4px' }} /></> : ''}
              <StatusBadge status={h.new_status} />
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6C757D', marginTop: '0.15rem' }}>
              {h.changed_by_name} · {formatDate(h.created_at?.split('T')[0])}
              {h.change_reason && <span> — {h.change_reason}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const LeaveRequestsPage = ({ user }) => {
  const [requests, setRequests]       = useState([]);
  const [balances, setBalances]       = useState([]);
  const [policies, setPolicies]       = useState([]);
  const [view, setView]               = useState('list');
  const [selected, setSelected]       = useState(null);
  const [isLoading, setIsLoading]     = useState(false);
  const [formData, setFormData]       = useState({ ...EMPTY_FORM });
  const [formError, setFormError]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]   = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [providers, setProviders]     = useState([]);

  // RBAC UI-keying: the HR-review action + elevated (all-employee) views are
  // keyed to leave:approve — the permission the leave:review handler enforces,
  // held by exactly admin + sysadmin today. This drops board_of_directors
  // (which holds neither leave:approve nor leave:read → the handler denies it).
  // NOTE: the all-employee VIEW columns are conservatively keyed to leave:approve
  // rather than leave:read to avoid BROADENING them to other leave:read holders;
  // that broadening decision is flagged for the owner, not made here.
  const canCreate = user?.permissions?.includes('leave:create');
  const canApprove = user?.permissions?.includes('leave:approve');
  const isAdmin = !!canApprove;

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => { loadRequests(); loadBalances(); loadPolicies(); loadProviders(); }, []);
  useEffect(() => { loadRequests(); }, [filterStatus, filterType]);

  const loadRequests = async () => {
    try {
      setIsLoading(true);
      const filters = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterType) filters.leaveType = filterType;
      const data = await window.zignagenixAPI.getLeaveRequests(filters);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load leave requests:', err); }
    finally { setIsLoading(false); }
  };

  const loadBalances = async () => {
    try {
      const data = await window.zignagenixAPI.getLeaveBalances();
      setBalances(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load balances:', err); }
  };

  const loadPolicies = async () => {
    try {
      const data = await window.zignagenixAPI.getLeavePolicies();
      setPolicies(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load policies:', err); }
  };

  const loadProviders = async () => {
    try {
      const data = await window.zignagenixAPI.getUsers();
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load providers:', err); }
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleNew = () => {
    setFormData({
      ...EMPTY_FORM,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    });
    setFormError('');
    setView('create');
  };

  const handleFormChange = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formData.startDate || !formData.endDate) {
      setFormError('Start and end dates are required'); return;
    }
    if (formData.endDate < formData.startDate) {
      setFormError('End date cannot be before start date'); return;
    }
    if (formData.leaveType === 'bereavement' && !formData.relationship?.trim()) {
      setFormError('Relationship to the deceased is required for bereavement leave'); return;
    }
    try {
      setIsLoading(true);
      setFormError('');
      const result = await window.zignagenixAPI.createLeaveRequest(formData);
      if (result?.error) {
        setFormError(result.message || 'Failed to submit request');
        return;
      }
      await loadRequests();
      await loadBalances();
      setView('list');
    } catch (err) {
      setFormError('Error: ' + err.message);
    } finally { setIsLoading(false); }
  };

  const handleViewDetail = async (req) => {
    try {
      const detail = await window.zignagenixAPI.getLeaveRequest(req.id);
      if (detail?.error) { alert(detail.message); return; }
      setSelected(detail);
      setReviewNotes('');
      setView('detail');
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      setIsLoading(true);
      await window.zignagenixAPI.cancelLeaveRequest(id);
      await loadRequests();
      await loadBalances();
      setView('list');
    } catch (err) { alert('Failed to cancel: ' + err.message); }
    finally { setIsLoading(false); }
  };

  const handleSupervisorReview = async (id, decision) => {
    try {
      setIsLoading(true);
      const result = await window.zignagenixAPI.supervisorReviewLeave(id, decision, reviewNotes);
      if (result?.error) { alert(result.message); return; }
      await loadRequests();
      await loadBalances();
      setView('list');
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setIsLoading(false); }
  };

  const handleHRReview = async (id, decision) => {
    try {
      setIsLoading(true);
      const result = await window.zignagenixAPI.reviewLeaveRequest(id, decision, reviewNotes);
      if (result?.error) { alert(result.message); return; }
      await loadRequests();
      await loadBalances();
      setView('list');
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setIsLoading(false); }
  };

  // Current policy for selected leave type
  const getActivePolicy = (type) => policies.find(p => p.leave_type === type);

  // ══════════════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'detail' && selected) {
    const canCancelThis = selected.user_id === user?.id &&
      !['cancelled', 'revoked', 'expired'].includes(selected.status);
    const canSupervisorReview = canApprove && selected.status === 'pending_supervisor';
    const canHRReview = isAdmin && ['pending_supervisor', 'pending_hr'].includes(selected.status);
    const policy = getActivePolicy(selected.leave_type);

    return (
      <div className="page">
        <button className="btn btn-ghost margin-bottom-20" onClick={() => setView('list')}>
          <ChevronLeft size={18} /> Back to List
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>
              {leaveLabel(selected.leave_type)} Request #{selected.id}
            </h1>
            <p style={{ color: '#6C757D', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Submitted by {selected.requester_name || 'Unknown'}
              {selected.is_fmla_eligible && (
                <span style={{ marginLeft: '0.5rem', background: '#dbeafe', color: '#1e40af', fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px' }}>
                  <Shield size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} /> FMLA Eligible
                </span>
              )}
            </p>
          </div>
          <StatusBadge status={selected.status} />
        </div>

        <div className="form-container" style={{ maxWidth: '760px' }}>
          {/* Core details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            <div className="detail-item">
              <p className="detail-item-label">Leave Type</p>
              <p className="detail-item-value">{leaveLabel(selected.leave_type)}</p>
            </div>
            <div className="detail-item">
              <p className="detail-item-label">Duration</p>
              <p className="detail-item-value">{daysBetween(selected.start_date, selected.end_date)} day(s)
                {selected.half_day && <span style={{ color: '#6C757D' }}> ({selected.half_day} half-day)</span>}
                {selected.is_intermittent && <span style={{ color: '#8b5cf6', fontWeight: 600 }}> (Intermittent)</span>}
              </p>
            </div>
            <div className="detail-item">
              <p className="detail-item-label">Start Date</p>
              <p className="detail-item-value">{formatDate(selected.start_date)}</p>
            </div>
            <div className="detail-item">
              <p className="detail-item-label">End Date</p>
              <p className="detail-item-value">{formatDate(selected.end_date)}</p>
            </div>
            {selected.hours_requested && (
              <div className="detail-item">
                <p className="detail-item-label">Hours Requested</p>
                <p className="detail-item-value">{selected.hours_requested} hrs</p>
              </div>
            )}
            {selected.urgency && selected.urgency !== 'standard' && (
              <div className="detail-item">
                <p className="detail-item-label">Urgency</p>
                <p className="detail-item-value" style={{ color: selected.urgency === 'emergency' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>
                  {selected.urgency.charAt(0).toUpperCase() + selected.urgency.slice(1)}
                </p>
              </div>
            )}
            {selected.reason_category && (
              <div className="detail-item">
                <p className="detail-item-label">Reason Category</p>
                <p className="detail-item-value">{selected.reason_category.replace(/_/g, ' ')}</p>
              </div>
            )}
            {selected.supervisor_name && (
              <div className="detail-item">
                <p className="detail-item-label">Supervisor</p>
                <p className="detail-item-value">{selected.supervisor_name}
                  {selected.supervisor_decision && (
                    <span style={{ marginLeft: '0.5rem' }}><StatusBadge status={selected.supervisor_decision} /></span>
                  )}
                </p>
              </div>
            )}
            {selected.relationship && (
              <div className="detail-item">
                <p className="detail-item-label">Relationship</p>
                <p className="detail-item-value">{selected.relationship}</p>
              </div>
            )}
          </div>

          {/* FMLA / Certification info */}
          {(selected.certification_required || selected.fmla_designation) && (
            <div style={{ marginTop: '1.25rem', ...NEU.cardSm, padding: '1rem', background: '#eff6ff' }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e40af', marginBottom: '0.5rem' }}>
                <Shield size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {selected.leave_type === 'fmla' ? 'FMLA Information' : 'Certification Status'}
              </div>
              {selected.fmla_designation && (
                <p style={{ fontSize: '0.85rem', color: '#374151', margin: '0.25rem 0' }}>
                  Designation: {FMLA_DESIGNATIONS.find(d => d.value === selected.fmla_designation)?.label || selected.fmla_designation}
                </p>
              )}
              <p style={{ fontSize: '0.85rem', color: '#374151', margin: '0.25rem 0' }}>
                Certification: {selected.certification_received ? 'Received' : selected.certification_required ? 'Required — Pending' : 'Not required'}
                {selected.certification_due_date && !selected.certification_received &&
                  <span style={{ color: '#f59e0b' }}> (due {formatDate(selected.certification_due_date)})</span>}
              </p>
              {selected.doctor_note_attached && (
                <p style={{ fontSize: '0.85rem', color: '#10b981', margin: '0.25rem 0' }}>Doctor's note attached</p>
              )}
            </div>
          )}

          {selected.reason && (
            <div style={{ marginTop: '1.25rem' }}>
              <p className="detail-item-label">Reason / Notes</p>
              <p className="detail-item-value" style={{ whiteSpace: 'pre-wrap' }}>{selected.reason}</p>
            </div>
          )}

          {/* Reviewer info */}
          {selected.reviewer_name && (
            <div style={{ marginTop: '1.25rem', ...NEU.cardSm, padding: '1rem', background: '#f0f9fb' }}>
              <p className="detail-item-label">HR Review by {selected.reviewer_name}</p>
              {selected.reviewer_notes && (
                <p className="detail-item-value" style={{ marginTop: '0.25rem' }}>{selected.reviewer_notes}</p>
              )}
            </div>
          )}

          {/* Return-to-work info */}
          {selected.return_confirmed && (
            <div style={{ marginTop: '1rem', ...NEU.cardSm, padding: '0.75rem 1rem', background: '#ecfdf5', fontSize: '0.85rem', color: '#065f46' }}>
              <RotateCcw size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Return-to-work confirmed: {formatDate(selected.return_date)}
              {selected.return_notes && <span> — {selected.return_notes}</span>}
            </div>
          )}

          {/* Status History Timeline */}
          <StatusTimeline history={selected.statusHistory} />

          {/* Policy info box */}
          {policy && (
            <div style={{ marginTop: '1.25rem', ...NEU.cardSm, padding: '0.75rem 1rem', background: '#f8fafc', fontSize: '0.82rem', color: '#475569' }}>
              <Info size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              <strong>Policy:</strong> {policy.display_name}
              {policy.max_days_per_year && <span> · Max {policy.max_days_per_year} days/yr</span>}
              {policy.is_paid ? ' · Paid' : ' · Unpaid'}
              {policy.min_notice_days > 0 && <span> · {policy.min_notice_days} day notice required</span>}
            </div>
          )}

          {/* Supervisor review panel */}
          {canSupervisorReview && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #f59e0b' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#f59e0b' }}>Supervisor Review</h3>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea className="form-input" value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..." rows={3} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={() => handleSupervisorReview(selected.id, 'approved')}
                  disabled={isLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Check size={16} /> Approve
                </button>
                <button className="btn-danger" onClick={() => handleSupervisorReview(selected.id, 'denied')}
                  disabled={isLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>
                  <X size={16} /> Deny
                </button>
              </div>
            </div>
          )}

          {/* HR review panel */}
          {canHRReview && !canSupervisorReview && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #2E8B98' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#2E8B98' }}>HR / Admin Final Review</h3>
              <div className="form-group">
                <label className="form-label">Review Notes (optional)</label>
                <textarea className="form-input" value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Add notes about your decision..." rows={3} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={() => handleHRReview(selected.id, 'approved')}
                  disabled={isLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Check size={16} /> Approve
                </button>
                <button className="btn-danger" onClick={() => handleHRReview(selected.id, 'denied')}
                  disabled={isLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>
                  <X size={16} /> Deny
                </button>
              </div>
            </div>
          )}

          {/* Cancel button */}
          {canCancelThis && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: 'none' }}>
              <button className="btn btn-ghost" onClick={() => handleCancel(selected.id)}
                disabled={isLoading} style={{ color: '#ef4444' }}>
                <Ban size={16} /> Cancel This Request
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // CREATE VIEW
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'create') {
    const activePolicy = getActivePolicy(formData.leaveType);
    const isBereavement = formData.leaveType === 'bereavement';
    const isMedical = ['medical', 'fmla'].includes(formData.leaveType);
    const isFmla = formData.leaveType === 'fmla';
    const days = daysBetween(formData.startDate, formData.endDate);

    return (
      <div className="page">
        <button className="btn btn-ghost margin-bottom-20" onClick={() => setView('list')}>
          <ChevronLeft size={18} /> Back to List
        </button>

        <h1 className="page-title page-title-spacious" style={{ margin: '0 0 0.25rem 0' }}>
          New Leave Request
        </h1>

        {/* Policy info banner */}
        {activePolicy && (
          <div style={{ marginBottom: '1.25rem', ...NEU.cardSm, padding: '0.75rem 1rem', background: '#f0f9fb', fontSize: '0.85rem', color: '#0c4a6e' }}>
            <Info size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            <strong>{activePolicy.display_name}:</strong> {activePolicy.description || ''}
            {activePolicy.max_days_per_year && <span> · Max {activePolicy.max_days_per_year} days/yr</span>}
            {activePolicy.is_paid ? ' · Paid' : ' · Unpaid'}
            {activePolicy.requires_certification && <span> · <strong>Certification required</strong></span>}
            {activePolicy.min_notice_days > 0 && <span> · {activePolicy.min_notice_days} day advance notice</span>}
          </div>
        )}

        <div className="form-container" style={{ maxWidth: '720px' }}>
          {formError && (
            <div className="alert--error" style={{ marginBottom: '1rem' }}>
              <AlertTriangle size={15} /> {formError}
            </div>
          )}

          <form className="form-grid">
            {/* Row 1: Type + Urgency */}
            <div className="form-group">
              <label className="form-label">Leave Type *</label>
              <select className="form-input" value={formData.leaveType}
                onChange={e => handleFormChange('leaveType', e.target.value)}>
                <optgroup label="Standard Leave">
                  {LEAVE_TYPES.filter(t => t.category === 'standard').map(t =>
                    <option key={t.value} value={t.value}>{t.label}</option>
                  )}
                </optgroup>
                <optgroup label="Medical Leave">
                  {LEAVE_TYPES.filter(t => t.category === 'medical').map(t =>
                    <option key={t.value} value={t.value}>{t.label}</option>
                  )}
                </optgroup>
                <optgroup label="Protected Leave (Federal)">
                  {LEAVE_TYPES.filter(t => t.category === 'protected').map(t =>
                    <option key={t.value} value={t.value}>{t.label}</option>
                  )}
                </optgroup>
                {isAdmin && (
                  <optgroup label="Administrative">
                    {LEAVE_TYPES.filter(t => t.category === 'admin').map(t =>
                      <option key={t.value} value={t.value}>{t.label}</option>
                    )}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Urgency</label>
              <select className="form-input" value={formData.urgency}
                onChange={e => handleFormChange('urgency', e.target.value)}>
                <option value="standard">Standard</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>

            {/* Row 2: Dates */}
            <div className="form-group">
              <label className="form-label">Start Date *</label>
              <input type="date" className="form-input" value={formData.startDate}
                onChange={e => handleFormChange('startDate', e.target.value)} required />
            </div>

            <div className="form-group">
              <label className="form-label">End Date *</label>
              <input type="date" className="form-input" value={formData.endDate}
                onChange={e => handleFormChange('endDate', e.target.value)} required />
            </div>

            {/* Row 3: Duration + Hours */}
            {formData.startDate && formData.endDate && formData.endDate >= formData.startDate && (
              <div className="form-group">
                <label className="form-label">Duration</label>
                <div style={{ padding: '0.6rem 1rem', background: '#f0f9fb', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 600, color: '#2E8B98' }}>
                  {days} day{days !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Hours Requested</label>
              <input type="number" step="0.5" min="0" className="form-input"
                value={formData.hoursRequested}
                onChange={e => handleFormChange('hoursRequested', e.target.value)}
                placeholder="e.g., 8, 16, 40" />
            </div>

            {/* Row 4: Half day + Intermittent */}
            <div className="form-group">
              <label className="form-label">Half Day</label>
              <select className="form-input" value={formData.halfDay}
                onChange={e => handleFormChange('halfDay', e.target.value)}>
                <option value="">Full Day(s)</option>
                <option value="morning">Morning Half</option>
                <option value="afternoon">Afternoon Half</option>
              </select>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
              <input type="checkbox" id="intermittent" checked={formData.isIntermittent}
                onChange={e => handleFormChange('isIntermittent', e.target.checked)} />
              <label htmlFor="intermittent" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                Intermittent leave (non-consecutive days)
              </label>
            </div>

            {/* FMLA-specific fields */}
            {isFmla && (
              <div className="form-group">
                <label className="form-label">FMLA Designation</label>
                <select className="form-input" value={formData.fmlaDesignation}
                  onChange={e => handleFormChange('fmlaDesignation', e.target.value)}>
                  <option value="">— Select —</option>
                  {FMLA_DESIGNATIONS.map(d =>
                    <option key={d.value} value={d.value}>{d.label}</option>
                  )}
                </select>
              </div>
            )}

            {/* Bereavement relationship */}
            {isBereavement && (
              <div className="form-group">
                <label className="form-label">Relationship to Deceased *</label>
                <input className="form-input" value={formData.relationship}
                  onChange={e => handleFormChange('relationship', e.target.value)}
                  placeholder="e.g., Parent, Spouse, Sibling" required />
              </div>
            )}

            {/* Medical certification */}
            {isMedical && (
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                <input type="checkbox" id="doctorNote" checked={formData.doctorNoteAttached}
                  onChange={e => handleFormChange('doctorNoteAttached', e.target.checked)} />
                <label htmlFor="doctorNote" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                  Doctor's note / medical certification attached
                </label>
              </div>
            )}

            {/* Reason category + free text */}
            <div className="form-group">
              <label className="form-label">Reason Category</label>
              <select className="form-input" value={formData.reasonCategory}
                onChange={e => handleFormChange('reasonCategory', e.target.value)}>
                {REASON_CATEGORIES.map(r =>
                  <option key={r.value} value={r.value}>{r.label}</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Supervisor (approver)</label>
              <select className="form-input" value={formData.supervisorId}
                onChange={e => handleFormChange('supervisorId', e.target.value)}>
                <option value="">— Select supervisor —</option>
                {providers.filter(p => p.id !== user?.id && p.is_active).map(p =>
                  <option key={p.id} value={p.id}>{p.display_name} ({p.role})</option>
                )}
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Reason / Notes</label>
              <textarea className="form-input" value={formData.reason}
                onChange={e => handleFormChange('reason', e.target.value)}
                placeholder="Provide details (HIPAA: do not include diagnosis or medical details here)..."
                rows={4} />
              <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                <Shield size={11} style={{ verticalAlign: 'middle' }} /> HIPAA Notice: Do not include specific medical diagnoses or treatment details.
                Medical certifications should be submitted separately to HR.
              </div>
            </div>

            <div className="patient-form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setView('list')} disabled={isLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
                <Save size={16} /> Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════════════════════════════════
  const pendingCount = requests.filter(r => ['pending_supervisor', 'pending_hr'].includes(r.status)).length;

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <CalendarOff size={28} color="#2E8B98" />
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Leave Requests</h1>
            {(isAdmin || canApprove) && pendingCount > 0 && (
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>
                {pendingCount} pending review
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <PrintButton />
          {canCreate && (
            <button className="btn btn-primary" onClick={handleNew}>
              <Plus size={16} /> New Request
            </button>
          )}
        </div>
      </div>

      {/* Balance cards */}
      <BalanceCards balances={balances} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 'auto', minWidth: '180px' }}
          value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending_supervisor">Pending Supervisor</option>
          <option value="pending_hr">Pending HR</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="form-input" style={{ width: 'auto', minWidth: '200px' }}
          value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Leave Types</option>
          {LEAVE_TYPES.map(t =>
            <option key={t.value} value={t.value}>{t.label}</option>
          )}
        </select>
      </div>

      {/* Table */}
      {isLoading && requests.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#6C757D', padding: '2rem' }}>Loading...</p>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6C757D' }}>
          <CalendarOff size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p style={{ fontSize: '1.05rem', fontWeight: 500 }}>No leave requests found</p>
          {canCreate && <p style={{ fontSize: '0.9rem' }}>Click "New Request" to submit one.</p>}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                {isAdmin && <th style={thStyle}>Employee</th>}
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Dates</th>
                <th style={thStyle}>Days</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Submitted</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(req => (
                <tr key={req.id} style={{ borderBottom: '1px solid #f0f0f0' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  {isAdmin && <td style={tdStyle}>{req.requester_name || '—'}</td>}
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500 }}>{leaveLabel(req.leave_type)}</span>
                    {req.is_intermittent && <span style={{ fontSize: '0.7rem', color: '#8b5cf6', marginLeft: 4 }}>INT</span>}
                  </td>
                  <td style={tdStyle}>
                    {formatDate(req.start_date)} — {formatDate(req.end_date)}
                  </td>
                  <td style={tdStyle}>{daysBetween(req.start_date, req.end_date)}</td>
                  <td style={tdStyle}><StatusBadge status={req.status} /></td>
                  <td style={tdStyle}>{formatDate(req.created_at?.split('T')[0])}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleViewDetail(req)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem' }}>
                      <Eye size={14} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '0.7rem 0.75rem', fontWeight: 600, fontSize: '0.8rem',
  color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.04em',
};

const tdStyle = {
  padding: '0.7rem 0.75rem', verticalAlign: 'middle',
};

export default LeaveRequestsPage;
